import { MILL_1, type MillId } from "@/setup/mill-config/mill-config";
import { ErrorModal } from "@dexteel/mesf-core";
import { Grid2 } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { AgGridReact } from "ag-grid-react";
import { useState } from "react";
import { Section, SectionStand } from "../../../../models/Section";
import { getSectionStands } from "../../../../repositories/SectionRepository";
import { SectionStandModal } from "./SectionStandModal";
import { useSectionStandsGridData } from "./useSectionStandsGridData";

type useSectionStandsProps = {
	sectionId?: number | null;
	assetId: MillId;
	onError: (error: Error) => void;
	onSuccess?: (data: any[]) => void;
};

export const useSectionStands = ({
	sectionId,
	assetId,
	onError,
	onSuccess,
}: useSectionStandsProps) => {
	return useQuery<SectionStand[], Error>({
		queryKey: ["sectionStands", sectionId, assetId],
		queryFn: ({ signal }) =>
			getSectionStands({ sectionId, defaultAreaId: assetId }, signal),
		onError: onError,
		onSuccess: onSuccess,
	});
};

type Props = {
	section: Section;
	assetId: MillId;
	disabled?: boolean;
};

export const SectionStandsGrid = ({
	section,
	assetId,
	disabled = false,
}: Props) => {
	const isM1 = assetId === MILL_1.id;

	const [error, setError] = useState("");
	const [openEdit, setOpenEdit] = useState<boolean>(false);
	const [selectedData, setSelectedData] = useState<SectionStand | null>();

	const { data: sectionStands, isLoading } = useSectionStands({
		sectionId: section?.SectionId,
		assetId,
		onError: (error) => setError(error.message),
	});

	const { columnDefs } = useSectionStandsGridData({
		isM1,
		setOpenEdit,
		setSelectedData,
	});
	const { defaultColDef } = useGridDefinitions({});

	return (
		<>
			<Grid2
				size={{ xs: 12, md: 12 }}
				sx={{
					height: "42vh",
				}}
			>
				<div style={{ height: "100%", width: "100%" }}>
					<AgGridReact<SectionStand>
						loading={isLoading}
						rowData={sectionStands}
						columnDefs={columnDefs}
						defaultColDef={defaultColDef}
						rowHeight={28}
						headerHeight={30}
						animateRows
						suppressContextMenu
						loadingOverlayComponent={CenteredLazyLoading}
						getRowId={(params) =>
							`${params.data.SectionId} ${params.data.StandId}`
						}
						gridOptions={{
							rowSelection: {
								mode: "singleRow",
								enableClickSelection: true,
								checkboxes: false,
							},
						}}
					/>
				</div>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<SectionStandModal
				show={openEdit}
				onHide={() => {
					setOpenEdit(false);
					setSelectedData(null);
				}}
				sectionStand={selectedData!}
				assetId={assetId}
			/>
		</>
	);
};
